import { pluralize } from "@suse-o11y/pluralize";
import type { Record } from "@suse-o11y/record";
import { Input } from "antd/es";
import React from "react";

import { Button } from "~/components/button";
import { SystemScreen } from "~/components/system-screen";

import type { LicenseError } from "../../../queries";
import { Link } from "../link";

import { isAboutToExpire } from "./is-about-to-expire";

const ERROR_MESSAGES: Record<string, string> = {
	missing: "The license key is missing!",
	invalid: "The license key is not valid!",
	expired: "Your license key has expired!",
	failure: "An unexpected error occurred!",
};

const pluralizeDays = pluralize({
	1: "1 day",
	more: (count) => `${count} days`,
});

const pluralizeMoreDays = pluralize({
	1: "1 more day",
	more: (count) => `${count} more days`,
});

const EXPIRED_THRESHOLD_DAYS = -14;
const getExpiredWorkingPeriod = (error: LicenseError, daysLeft: undefined | number): null | number => {
	if (error === "expired" && daysLeft !== undefined && daysLeft <= 0 && daysLeft > EXPIRED_THRESHOLD_DAYS) {
		return daysLeft - EXPIRED_THRESHOLD_DAYS;
	}

	return null;
};

export interface LicenseEntryProps {
	error: LicenseError;
	daysLeft: undefined | number;
	onRegister: React.Dispatch<string>;
	onClose: VoidFunction;
}

export const LicenseEntry: React.FC<LicenseEntryProps> = ({ error, daysLeft, onRegister, onClose }) => {
	const [licenseKey, setLicenseKey] = React.useState("");

	const errorMessage = error ? ERROR_MESSAGES[error] : undefined;
	const notYetExpired = error == null && daysLeft != null && isAboutToExpire(daysLeft);
	const expiredWorkingPeriod = getExpiredWorkingPeriod(error, daysLeft);

	return (
		<SystemScreen
			alertContent={notYetExpired ? `Your license key will expire in ${pluralizeDays(daysLeft)}!` : errorMessage}
			alertVariant={notYetExpired ? "warning" : "error"}
			className="min-h-full"
		>
			<form
				className="flex flex-col items-center"
				data-testid="license-entry"
				noValidate
				onSubmit={(event) => {
					onRegister(licenseKey);
					event.preventDefault();
				}}
			>
				<div>
					{expiredWorkingPeriod != null && (
						<>
							You can still use SUSE Observability for{" "}
							<strong className="font-medium">{pluralizeMoreDays(expiredWorkingPeriod)}</strong>.{" "}
						</>
					)}
					Please enter a valid license key or contact support as soon as possible.
				</div>

				<div className="mt-7 flex items-center gap-6">
					<Input.Group className="w-auto text-left" compact size="large">
						<Input
							autoComplete="new license key"
							autoFocus
							className="h-10 w-64!"
							name="licenseKey"
							onChange={(event) => setLicenseKey(event.target.value)}
							placeholder="00000 - 00000 - 00000"
							size="large"
							type="text"
							value={licenseKey}
						/>
						<Button className="uppercase" disabled={!licenseKey} size="lg" type="submit" variant="primary">
							Register
						</Button>
					</Input.Group>

					{(notYetExpired || expiredWorkingPeriod != null) && (
						<Link el="button" onClick={onClose} theme="secondary" type="button">
							Not now
						</Link>
					)}
				</div>
			</form>
		</SystemScreen>
	);
};
